"use client";

import { useState, useRef, useEffect } from "react";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogDescription,
} from "@/components/ui/Dialog";
import { Badge } from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import {
	Copy,
	MapPin,
	Check,
	X,
	Lock,
	Loader2,
	Home,
	Building,
	DollarSign,
	User,
	FileText,
	Download,
	ExternalLink,
	TrendingUp,
	Image as ImageIcon,
	Mail,
} from "lucide-react";
import { toast } from "sonner";
import { useOnboarding } from "@/contexts/OnboardingContext";
import {
	createCheckoutSessionWithAuth,
	STRIPE_PRO_PRICE_ID,
} from "@/services/stripe-api";
import type { NearbyPropertiesResponse } from "@/services/properties-api";
import type { ScrapedParcelData } from "@/types/parcel";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { getColumnLabel } from "@/components/enrichment/enrichmentColumns";
import RequestPhotosModal from "./RequestPhotosModal";
import Image from "next/image";
import { checkPropertyMediaRequest } from "@/services/property-media-api";
import { PhotoRequestAlreadySentBlock } from "./PhotoRequestAlreadySentBlock";

const MEDIA_BASE_URL =
	process.env.NEXT_PUBLIC_MEDIA_BASE_URL || "https://media.taxlienvest.com/";

// Use the parcelData type from the API response
export type ParcelData = NonNullable<
	NearbyPropertiesResponse["data"][0]["parcelData"]
>;

interface ParcelDataViewerProps {
	isOpen: boolean;
	onClose: () => void;
	parcelData: ParcelData | null;
	scrapedParcelData?: ScrapedParcelData | null;
	propertyId?: string;
	realEstateId?: string;
	state?: string;
	county?: string;
	medias?: string[];
}

const formatCurrency = (value: number | null | undefined) => {
	if (
		value == null ||
		(typeof value === "number" &&
			(Number.isNaN(value) || !Number.isFinite(value)))
	)
		return "—";
	if (value === 0) return "—";
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(value);
};

/** Sale amount: no $NaN; show "—" when no data or non-numeric text */
const formatSaleAmount = (value: unknown): string => {
	if (value == null || value === "") return "—";
	const str = String(value).trim();
	if (!str) return "—";
	const num = parseFloat(str.replace(/,/g, ""));
	if (!Number.isFinite(num)) return "—";
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(num);
};

const formatDate = (dateString: string | null | undefined) => {
	if (!dateString) return "N/A";
	try {
		const date = new Date(dateString);
		if (Number.isNaN(date.getTime())) return dateString;
		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	} catch {
		return dateString;
	}
};

const formatValue = (value: unknown) => {
	if (value === null || value === undefined || value === "") return "N/A";
	return String(value);
};

interface DataFieldProps {
	label: string;
	value: unknown;
	copyable?: boolean;
	onCopy: (text: string, fieldName: string) => void;
	copiedField: string | null;
}

const DataField = ({
	label,
	value,
	copyable = false,
	onCopy,
	copiedField,
}: DataFieldProps) => {
	const displayValue = formatValue(value);
	const isCopied = copiedField === label;

	return (
		<div className="flex justify-between items-start gap-4 py-2.5 px-6 border-b border-gray-100 last:border-0 w-full">
			<span className="text-gray-600 text-xs min-w-[120px] flex-shrink-0">
				{label}
			</span>
			<div className="flex items-center gap-2 flex-1 min-w-0 justify-end pl-2 pr-2">
				<span className="text-gray-900 text-xs text-right break-words">
					{displayValue}
				</span>
				{displayValue !== "N/A" && (
					<button
						onClick={() => onCopy(displayValue, label)}
						className="text-[#000000] hover:text-[#8ee63f] transition-colors flex-shrink-0"
						aria-label={`Copy ${label}`}
					>
						{isCopied ? (
							<Check className="w-3 h-3" />
						) : (
							<Copy className="w-3 h-3" />
						)}
					</button>
				)}
			</div>
		</div>
	);
};

// Configuration types
type FieldConfig = {
	label: string;
	key?: keyof ParcelData | string; // key can be a direct property or handled via getter
	getValue?: (data: ParcelData) => unknown; // Custom getter
	copyable?: boolean;
};

// Field Configurations
const PROPERTY_CHARACTERISTICS: FieldConfig[] = [
	{ label: "Use Description", key: "usedesc" },
	{ label: "Use Code", key: "usecode" },
	{ label: "Year Built", key: "yearbuilt" },
	{ label: "Structure Style", key: "structstyle" },
	{ label: "Number of Stories", key: "numstories" },
	{ label: "Number of Rooms", key: "numrooms" },
	{ label: "Number of Units", key: "numunits" },
	{ label: "Square Feet (GIS)", key: "ll_gissqft" },
	{ label: "Acreage (GIS)", key: "ll_gisacre" },
	{ label: "Zoning", key: "zoning" },
	{ label: "Zoning Description", key: "zoning_description" },
	{ label: "Subdivision", key: "subdivision" },
	{ label: "Lot", key: "lot" },
	{ label: "Block", key: "block" },
];

const FINANCIAL_DETAILS: FieldConfig[] = [
	{
		label: "Property Value",
		getValue: (d) => formatCurrency(d.parval as number | null | undefined),
	},
	{
		label: "Land Value",
		getValue: (d) =>
			formatCurrency(typeof d.landval === "number" ? d.landval : null),
	},
	{
		label: "Improvement Value",
		getValue: (d) =>
			formatCurrency(
				typeof d.improvval === "number" ? d.improvval : null
			),
	},
	{
		label: "Agricultural Value",
		getValue: (d) =>
			formatCurrency(typeof d.agval === "number" ? d.agval : null),
	},
	{
		label: "Tax Amount",
		getValue: (d) => formatCurrency(d.taxamt as number | null | undefined),
	},
	{ label: "Tax Year", key: "taxyear" },
	{
		label: "Sale Price",
		getValue: (d) =>
			formatCurrency(d.saleprice as number | null | undefined),
	},
	{
		label: "Sale Date",
		getValue: (d) => formatDate(d.saledate as string | null | undefined),
	},
	{ label: "Value Type", key: "parvaltype" },
];

const LOCATION_ADDRESS: FieldConfig[] = [
	{ label: "Full Address", key: "address", copyable: true },
	{ label: "City", key: "scity" },
	{ label: "State", key: "state2" },
	{ label: "ZIP Code", key: "szip5" },
	{
		label: "County",
		getValue: (d) => String(d.county ?? "").toUpperCase(),
	},
	{ label: "Latitude", key: "lat", copyable: true },
	{ label: "Longitude", key: "lon", copyable: true },
	{ label: "Street Number", key: "saddno" },
	{ label: "Street Name", key: "saddstr" },
	{ label: "Street Type", key: "saddsttyp" },
	{ label: "Unit", key: "sunit" },
];

const OWNER_MAILING: FieldConfig[] = [
	{ label: "Primary Owner", key: "owner", copyable: true },
	{ label: "Owner 2", key: "owner2" },
	{ label: "Owner 3", key: "owner3" },
	{ label: "Owner 4", key: "owner4" },
	{ label: "Owner Type", key: "owntype" },
	{ label: "Previous Owner", key: "previous_owner" },
	{ label: "Care Of", key: "careof" },
];

const MAILING_ADDRESS: FieldConfig[] = [
	{ label: "Mailing Address", key: "mailadd" },
	{ label: "Mailing City", key: "mail_city" },
	{ label: "Mailing State", key: "mail_state2" },
	{ label: "Mailing ZIP", key: "mail_zip" },
	{ label: "Mailing Country", key: "mail_country" },
];

const GEOGRAPHIC_CENSUS: FieldConfig[] = [
	{ label: "GEO ID", key: "geoid" },
	{ label: "Census Tract", key: "census_tract" },
	{ label: "Census Block", key: "census_block" },
	{ label: "Census Block Group", key: "census_blockgroup" },
	{ label: "Census ZCTA", key: "census_zcta" },
	{ label: "Opportunity Zone", key: "qoz" },
	{ label: "QOZ Tract", key: "qoz_tract" },
	{ label: "Neighborhood", key: "neighborhood" },
];

const LEGAL_INFO: FieldConfig[] = [
	{ label: "Parcel Number", key: "parcelnumb", copyable: true },
	{
		label: "Parcel (No Formatting)",
		key: "parcelnumb_no_formatting",
	},
	{ label: "Account Number", key: "account_number" },
	{ label: "Tax ID", key: "tax_id" },
	{ label: "Alternative Parcel 1", key: "alt_parcelnumb1" },
	{ label: "Alternative Parcel 2", key: "alt_parcelnumb2" },
	{ label: "Alternative Parcel 3", key: "alt_parcelnumb3" },
	{ label: "State Parcel Number", key: "state_parcelnumb" },
	{ label: "Book", key: "book" },
	{ label: "Page", key: "page" },
	{ label: "Plat", key: "plat" },
];

const ProBadge = () => (
	<Badge className="bg-[#A7FF5E] text-[#16251c] hover:bg-[#A7FF5E] border-none ml-2 px-1.5 py-0.5 h-5 text-[10px] font-bold tracking-wider">
		PRO
	</Badge>
);

export default function ParcelDataViewer({
	isOpen,
	onClose,
	parcelData,
	scrapedParcelData,
	propertyId,
	state,
	county,
	medias,
	realEstateId,
}: ParcelDataViewerProps) {
	const router = useRouter();
	type TabId =
		| "overview"
		| "property"
		| "financial"
		| "owner"
		| "location"
		| "legal"
		| "photos"
		| "scraped"
		| "links";
	const [copiedField, setCopiedField] = useState<string | null>(null);
	const [isUpgrading, setIsUpgrading] = useState(false);
	const [activeTab, setActiveTab] = useState<TabId>("overview");
	const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
	const [reqPhotosModal, setReqPhotosModal] = useState(false);
	const [checkingPhotoRequest, setCheckingPhotoRequest] = useState(false);
	const [requestAlreadySent, setRequestAlreadySent] = useState<
		boolean | null
	>(null);
	const [checkingRequest, setCheckingRequest] = useState(false);

	const { isSubscribed } = useOnboarding();

	// Check once when parcel viewer is opened with a valid realEstateId (for Photos tab)
	useEffect(() => {
		if (!isOpen || !realEstateId) return;
		let cancelled = false;
		setCheckingRequest(true);
		checkPropertyMediaRequest(realEstateId)
			.then((res) => {
				if (!cancelled) setRequestAlreadySent(!res.canRequest);
			})
			.catch(() => {
				if (!cancelled) setRequestAlreadySent(null);
			})
			.finally(() => {
				if (!cancelled) setCheckingRequest(false);
			});
		return () => {
			cancelled = true;
		};
	}, [isOpen, realEstateId]);

	const handleRequestPhotosClick = async () => {
		if (!realEstateId) {
			toast.error("Property identifier not available.");
			return;
		}
		setCheckingPhotoRequest(true);
		try {
			const res = await checkPropertyMediaRequest(realEstateId);
			if (!res.canRequest) {
				toast.info("Request already initiated for this property.");
				return;
			}
			// Close parcel dialog first — it uses z-[10050]; request modal was z-9999 and stayed behind.
			onClose();
			setReqPhotosModal(true);
		} catch (e) {
			toast.error(
				e instanceof Error ? e.message : "Failed to check request."
			);
		} finally {
			setCheckingPhotoRequest(false);
		}
	};

	const hasScrapedData = Boolean(scrapedParcelData);
	// All $0 means no data
	const parseAmount = (value: string | number | null | undefined): number => {
		if (value == null || value === "") return 0;
		const num =
			typeof value === "number"
				? value
				: parseFloat(String(value).replace(/[,$]/g, ""));
		return Number.isFinite(num) ? num : 0;
	};
	const hasRecentSalesData =
		(scrapedParcelData?.recent_sales?.length ?? 0) > 0 &&
		scrapedParcelData!.recent_sales!.some(
			(s) => parseAmount(s.sale_amount) > 0
		);
	const deedHistory = scrapedParcelData?.details_page?.json?.deed_history;
	const hasDeedData =
		(deedHistory?.length ?? 0) > 0 &&
		deedHistory!.some((d) => parseAmount(d.sale_amount) > 0);
	const hasScrapedSections =
		hasScrapedData &&
		(scrapedParcelData?.property_characteristics?.json ||
			hasRecentSalesData ||
			hasDeedData);
	const hasLinksSection =
		(scrapedParcelData as Record<string, unknown> | null)?.section_urls ||
		(parcelData && (propertyId ?? state ?? county));

	// Debug logging
	if (process.env.NODE_ENV === "development" && isOpen) {
		console.log("ParcelDataViewer - scrapedParcelData:", scrapedParcelData);
		console.log(
			"ParcelDataViewer - property_characteristics:",
			scrapedParcelData?.property_characteristics
		);
	}

	const handleUpgrade = async () => {
		setIsUpgrading(true);
		try {
			const { url } =
				await createCheckoutSessionWithAuth(STRIPE_PRO_PRICE_ID);
			window.location.href = url;
		} catch (error) {
			console.error("Failed to start checkout:", error);
			// Don't show error if redirecting to login
			if (
				error instanceof Error &&
				error.message !== "Redirecting to login"
			) {
				toast.error("Failed to start checkout. Please try again.");
			}
			setIsUpgrading(false);
		}
	};

	if (!parcelData) return null;

	const copyToClipboard = (text: string, fieldName: string) => {
		navigator.clipboard.writeText(text);
		setCopiedField(fieldName);
		toast.success(`${fieldName} copied to clipboard`);
		setTimeout(() => setCopiedField(null), 2000);
	};

	const openGoogleMaps = () => {
		window.open(
			`https://www.google.com/maps?q=${parcelData.lat},${parcelData.lon}`,
			"_blank"
		);
	};

	// Helper to render fields from config
	const renderFields = (configs: FieldConfig[]) => {
		return configs.map((config) => {
			let value: unknown;
			if (config.getValue) {
				value = config.getValue(parcelData);
			} else if (config.key) {
				value = parcelData[config.key];
			}

			return (
				<DataField
					key={config.label}
					label={config.label}
					value={value}
					copyable={config.copyable}
					onCopy={copyToClipboard}
					copiedField={copiedField}
				/>
			);
		});
	};

	const propertyType =
		String(parcelData.usedesc ?? "").trim() ||
		String(parcelData.usecode ?? "").trim() ||
		"Property";

	const apn =
		String(parcelData.parcelnumb ?? "").trim() ||
		String(parcelData.parcelnumb_no_formatting ?? "").trim() ||
		"N/A";

	const fullAddress =
		`${String(parcelData.address ?? "").trim()}, ${String(parcelData.scity ?? "").trim()}, ${String(parcelData.state2 ?? "").trim()} ${String(parcelData.szip5 ?? "").trim()}`.replace(
			/^,\s*|,\s*$/g,
			""
		);

	const renderProOverlay = (
		title: string,
		message: string,
		btnText: string
	) => (
		<div className="absolute inset-0 z-10 backdrop-blur-[6px] bg-white/30 flex items-center justify-center p-6">
			<div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 flex flex-col items-center text-center max-w-[280px]">
				<div className="w-12 h-12 rounded-full bg-[#A7FF5E]/20 flex items-center justify-center mb-4">
					<Lock className="w-5 h-5 text-[#16251c]" />
				</div>
				<h4 className="font-bold text-[#16251c] mb-2">PRO Feature</h4>
				<p className="text-sm text-gray-500 mb-6">{message}</p>
				<Button
					data-testid="unlock-property-data-button"
					onClick={handleUpgrade}
					disabled={isUpgrading}
					className="w-full bg-[#16251c] text-white hover:bg-[#16251c]/90 disabled:opacity-70 disabled:cursor-not-allowed"
				>
					{isUpgrading ? (
						<Loader2 className="h-4 w-4 animate-spin mr-2" />
					) : null}
					{isUpgrading ? "Redirecting..." : btnText}
				</Button>
				<Link
					href="/signup"
					data-testid="start-trial-button"
					className="mt-3 w-full inline-flex items-center justify-center rounded-xl border-2 border-[#16251c] bg-white py-3 px-4 text-sm font-semibold text-[#16251c] hover:bg-gray-50 transition-colors"
				>
					Start 14-Day Free Trial
				</Link>
				<Link
					href="/signup"
					data-testid="trial-available-link"
					className="mt-2 text-xs font-medium text-[#417714] hover:underline"
				>
					14-Day Free Trial Available
				</Link>
			</div>
		</div>
	);

	const tabs: { id: TabId; label: string; icon: typeof Home }[] = [
		{ id: "overview", label: "Overview", icon: Home },
		{ id: "property", label: "Property", icon: Building },
		{ id: "financial", label: "Financial", icon: DollarSign },
		{ id: "owner", label: "Owner", icon: User },
		{ id: "location", label: "Location", icon: MapPin },
		{ id: "legal", label: "Legal", icon: FileText },
		{ id: "photos", label: "Photos", icon: ImageIcon },
		...(hasScrapedSections
			? [
					{
						id: "scraped" as const,
						label: "Building & Sales",
						icon: FileText,
					},
				]
			: []),
		...(hasLinksSection
			? [{ id: "links" as const, label: "Links", icon: ExternalLink }]
			: []),
	];

	return (
		<>
			<Dialog open={isOpen} onOpenChange={onClose}>
				<DialogContent
					data-testid="parcel-details-dialog"
					className="max-w-[96vw] w-[96vw] max-h-[90vh] h-[90vh] p-0 gap-0 flex flex-col overflow-hidden [&>button]:hidden"
				>
					<DialogTitle className="sr-only">
						Parcel Details for {apn}
					</DialogTitle>
					<DialogDescription className="sr-only">
						Detailed information about property at {fullAddress}
					</DialogDescription>
					{/* Compact Header - Fixed */}
					<div className="bg-[#0f1b13] px-4 py-2 flex items-center justify-between border-b border-gray-700 min-h-[44px] flex-shrink-0">
						<div className="flex items-center gap-2 min-w-0 flex-1">
							<Badge className="bg-[#A7FF5E] text-[#0f1b13] hover:bg-[#A7FF5E] px-1.5 py-0.5 text-[10px] font-semibold uppercase flex-shrink-0">
								{propertyType}
							</Badge>
							<div className="min-w-0 flex-1">
								<h2
									data-testid="parcel-number"
									className="text-xs font-bold text-white truncate"
								>
									{apn}
								</h2>
								<p
									data-testid="parcel-address"
									className="text-[11px] text-gray-300 truncate"
								>
									{fullAddress || "Address not available"}
								</p>
							</div>
						</div>
						<div className="flex items-center gap-1.5 flex-shrink-0">
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={openGoogleMaps}
								className="bg-transparent border-gray-600 text-white hover:bg-white/10 h-6 text-[11px] px-2"
								data-testid="view-on-google-maps-button"
							>
								<MapPin className="w-3 h-3 mr-1" />
								View on Google Maps
							</Button>
							{propertyId && state && county && isSubscribed && (
								<Button
									type="button"
									variant="outline"
									size="sm"
									data-testid="explore-parcel-data-button"
									onClick={() => {
										window.open(
											`/${state}/${county}/property/${propertyId}/parcel`,
											"_blank"
										);
										onClose();
									}}
									className="bg-transparent border-gray-600 text-white hover:bg-white/10 h-6 text-[11px] px-2"
								>
									<ExternalLink className="w-3 h-3 mr-1" />
									Explore Parcel data
								</Button>
							)}
							<button
								data-testid="close-parcel-details"
								onClick={onClose}
								className="p-1 hover:bg-white/10 rounded transition-colors text-white ml-0.5"
								aria-label="Close"
							>
								<X className="w-3.5 h-3.5" />
							</button>
						</div>
					</div>
					{/* Body: column with row (sidebar+main) + footer */}
					<div className="flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden">
						<div className="flex flex-1 min-h-0 min-w-0 overflow-hidden">
							{/* Sidebar */}
							<aside className="w-60 bg-gray-50 border-r border-gray-200 flex flex-col flex-shrink-0 overflow-hidden">
								<div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
									<h4 className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
										Data Sections
									</h4>
								</div>
								<nav
									data-testid="parcel-modal-sidebar"
									className="flex-1 overflow-y-auto px-3 py-2 space-y-1"
								>
									{tabs.map((tab) => {
										const Icon = tab.icon;
										return (
											<button
												key={tab.id}
												type="button"
												data-testid="parcel-tab-button"
												aria-label={tab.label}
												onClick={() =>
													setActiveTab(tab.id)
												}
												className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
													activeTab === tab.id
														? "bg-[#A7FF5E] text-[#0f1b13] shadow-sm"
														: "text-gray-700 hover:bg-gray-200"
												}`}
											>
												<Icon className="w-4 h-4 flex-shrink-0" />
												<span>{tab.label}</span>
											</button>
										);
									})}
								</nav>
							</aside>

							{/* Main content - scrollable */}
							<div className="flex-1 min-w-0 bg-white overflow-y-auto overflow-x-auto min-h-0">
								<div className="p-8 w-full min-w-0">
									{/* Tab: Overview */}
									{activeTab === "overview" && (
										<div
											className="space-y-6"
											data-testid="overview-section"
										>
											<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
												<div
													data-testid="sale-price-card"
													className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
												>
													<div className="text-gray-600 text-xs uppercase tracking-wider mb-2">
														Sale Price
													</div>
													<div className="text-[#417714] text-2xl font-bold">
														{formatCurrency(
															parcelData.saleprice as
																| number
																| null
																| undefined
														) ||
															formatCurrency(
																parcelData.market_value as
																	| number
																	| null
																	| undefined
															) ||
															formatCurrency(
																parcelData.parval as
																	| number
																	| null
																	| undefined
															)}
													</div>
												</div>
												<div
													data-testid="assessed-value-card"
													className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
												>
													<div className="text-gray-600 text-xs uppercase tracking-wider mb-2">
														Assessed Value
													</div>
													<div className="text-[#417714] text-2xl font-bold">
														{formatCurrency(
															parcelData.parval as
																| number
																| null
																| undefined
														)}
													</div>
												</div>
												<div
													data-testid="year-built-card"
													className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
												>
													<div className="text-gray-600 text-xs uppercase tracking-wider mb-2">
														Year Built
													</div>
													<div className="text-[#417714] text-2xl font-bold">
														{String(
															parcelData.yearbuilt ??
																"N/A"
														)}
													</div>
												</div>
											</div>
											<div className="bg-white rounded-lg border border-gray-200 w-full">
												<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
													<h3 className="text-[11px] font-semibold text-gray-900">
														Property Overview
													</h3>
												</div>
												<div className="divide-y divide-gray-100 w-full">
													{renderFields([
														{
															label: "Parcel Number",
															key: "parcelnumb",
															copyable: true,
														},
														{
															label: "Property Address",
															getValue: () =>
																fullAddress,
															copyable: true,
														},
														{
															label: "Owner",
															key: "owner",
															copyable: true,
														},
														{
															label: "Use Description",
															key: "usedesc",
														},
														{
															label: "Property Type",
															key: "usedesc",
														},
														{
															label: "Year Built",
															key: "yearbuilt",
														},
														{
															label: "Sale Price",
															getValue: (d) =>
																formatCurrency(
																	d.saleprice as
																		| number
																		| null
																		| undefined
																),
														},
														{
															label: "Assessed Value",
															getValue: (d) =>
																formatCurrency(
																	d.parval as
																		| number
																		| null
																		| undefined
																),
														},
														{
															label: "County",
															getValue: (d) =>
																String(
																	d.county ??
																		""
																).toUpperCase(),
														},
														{
															label: "Latitude / Longitude",
															getValue: (d) =>
																`${d.lat}, ${d.lon}`,
															copyable: true,
														},
													])}
												</div>
											</div>
										</div>
									)}

									{/* Tab: Property */}
									{activeTab === "property" && (
										<div className="space-y-6 relative">
											{!isSubscribed &&
												renderProOverlay(
													"PRO Feature",
													"Detailed property characteristics are available with a PRO subscription.",
													"Unlock Property Data"
												)}
											<div
												className="bg-white rounded-lg border border-gray-200 w-full"
												data-testid="property-characteristics-section"
											>
												<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
													<h3 className="text-[11px] font-semibold text-gray-900">
														Property Characteristics
													</h3>
												</div>
												<div className="divide-y divide-gray-100 w-full">
													{renderFields(
														PROPERTY_CHARACTERISTICS
													)}
												</div>
											</div>
										</div>
									)}

									{/* Tab: Financial */}
									{activeTab === "financial" && (
										<div className="space-y-6 relative">
											{!isSubscribed &&
												renderProOverlay(
													"PRO Feature",
													"Financial details and tax history are available with a PRO subscription.",
													"Unlock Financial Data"
												)}
											<div
												className="bg-white rounded-lg border border-gray-200 w-full"
												data-testid="financial-details-section"
											>
												<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
													<h3 className="text-[11px] font-semibold text-gray-900">
														Financial Details
													</h3>
												</div>
												<div className="divide-y divide-gray-100 w-full">
													{renderFields(
														FINANCIAL_DETAILS
													)}
												</div>
											</div>
										</div>
									)}

									{/* Tab: Owner */}
									{activeTab === "owner" && (
										<div
											className="space-y-6 relative"
											data-testid="owner-mailing-section"
										>
											{!isSubscribed &&
												renderProOverlay(
													"PRO Feature",
													"Owner information and mailing addresses are exclusive to PRO members.",
													"Unlock Owner Data"
												)}
											<div className="bg-white rounded-lg border border-gray-200 w-full">
												<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
													<h3 className="text-[11px] font-semibold text-gray-900">
														Owner Information
													</h3>
												</div>
												<div className="divide-y divide-gray-100 w-full">
													{renderFields(
														OWNER_MAILING
													)}
												</div>
											</div>
											<div className="bg-white rounded-lg border border-gray-200 w-full">
												<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
													<h3 className="text-[11px] font-semibold text-gray-900">
														Mailing Address
													</h3>
												</div>
												<div className="divide-y divide-gray-100 w-full">
													{renderFields(
														MAILING_ADDRESS
													)}
												</div>
											</div>
										</div>
									)}

									{/* Tab: Location */}
									{activeTab === "location" && (
										<div className="space-y-6">
											<div
												className="bg-white rounded-lg border border-gray-200 w-full"
												data-testid="location-address-section"
											>
												<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
													<h3 className="text-[11px] font-semibold text-gray-900">
														Location & Address
													</h3>
												</div>
												<div className="divide-y divide-gray-100 w-full">
													{renderFields(
														LOCATION_ADDRESS
													)}
												</div>
											</div>
											<div
												className="bg-white rounded-lg border border-gray-200 w-full"
												data-testid="geographic-census-section"
											>
												<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
													<h3 className="text-[11px] font-semibold text-gray-900">
														Geographic & Census Data
													</h3>
												</div>
												<div className="divide-y divide-gray-100 w-full">
													{renderFields(
														GEOGRAPHIC_CENSUS
													)}
												</div>
											</div>
										</div>
									)}

									{/* Tab: Photos */}
									{activeTab === "photos" && (
										<div
											className="space-y-6"
											data-testid="photos-section"
										>
											{medias && medias.length > 0 ? (
												<div className="columns-1 sm:columns-2 md:columns-3 lg:columns-3 gap-4">
													{medias.map(
														(photo, index) => (
															<div
																key={index}
																className="break-inside-avoid mb-4"
																onClick={() =>
																	setSelectedPhoto(
																		photo
																	)
																}
															>
																<div className="relative aspect-[4/3] w-full group overflow-hidden rounded-lg border border-gray-200 shadow-sm hover:shadow-xl transition-all cursor-pointer">
																	<Image
																		src={`${MEDIA_BASE_URL}${photo}`}
																		alt={`Property photo ${index + 1}`}
																		fill
																		className="object-cover group-hover:scale-105 transition-transform duration-300"
																		sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
																	/>
																	<div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
																</div>
															</div>
														)
													)}
												</div>
											) : checkingRequest ? (
												<div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
													<div className="flex flex-col items-center gap-4">
														<Loader2
															className="w-10 h-10 animate-spin text-[#417714]"
															aria-hidden
														/>
														<p className="text-sm text-gray-600">
															Checking request
															status…
														</p>
													</div>
												</div>
											) : requestAlreadySent === true ? (
												<div className="bg-white rounded-lg border border-gray-200 p-6">
													<PhotoRequestAlreadySentBlock
														address={fullAddress}
													/>
												</div>
											) : (
												<div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
													<div className="flex flex-col items-center gap-4">
														<div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
															<ImageIcon className="w-8 h-8 text-gray-400" />
														</div>
														<div>
															<h3 className="text-lg font-semibold text-gray-900 mb-2">
																No Photos
																Available
															</h3>
															<p className="text-sm text-gray-600 mb-6">
																We don&apos;t
																have any photos
																for this
																property yet.
															</p>
															<Button
																data-testid="request-photos-button"
																onClick={
																	handleRequestPhotosClick
																}
																disabled={
																	checkingPhotoRequest
																}
																className="bg-[#A7FF5E] hover:bg-[#8ee63f] text-[#16251c] font-semibold inline-flex items-center gap-2"
															>
																{checkingPhotoRequest ? (
																	<>
																		<Loader2
																			className="w-5 h-5 animate-spin"
																			aria-hidden
																		/>
																		Checking…
																	</>
																) : (
																	"Request Photos"
																)}
															</Button>
														</div>
													</div>
												</div>
											)}
										</div>
									)}

									{/* Tab: Legal */}
									{activeTab === "legal" && (
										<div className="space-y-6 relative">
											{!isSubscribed &&
												renderProOverlay(
													"PRO Feature",
													"Legal descriptions and parcel numbers are available with a PRO subscription.",
													"Unlock Legal Data"
												)}
											<div
												className="bg-white rounded-lg border border-gray-200 w-full"
												data-testid="legal-information-section"
											>
												<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
													<h3 className="text-[11px] font-semibold text-gray-900">
														Legal Information
													</h3>
												</div>
												<div className="p-6">
													<div className="bg-gray-50 p-5 rounded-lg border border-gray-200 mb-6">
														<div className="text-gray-500 text-xs uppercase tracking-wider mb-2">
															Legal Description
														</div>
														<div className="text-gray-900 text-sm leading-relaxed">
															{String(
																parcelData.legaldesc ??
																	""
															) || "N/A"}
														</div>
													</div>
													<div className="divide-y divide-gray-100 w-full">
														{renderFields(
															LEGAL_INFO
														)}
													</div>
												</div>
											</div>
										</div>
									)}

									{/* Tab: Scraped (Building & Sales) */}
									{activeTab === "scraped" &&
										hasScrapedSections &&
										scrapedParcelData && (
											<div className="space-y-6">
												{scrapedParcelData
													.property_characteristics
													?.json &&
													(() => {
														const pc =
															scrapedParcelData
																.property_characteristics!
																.json!;
														const na = "N/A";
														const resSummaryKeys = [
															"style",
															"total_area",
															"year_built",
															"card_number",
														] as const;
														const resSummary =
															pc.residential_summary
																? resSummaryKeys
																		.map(
																			(
																				k
																			) => ({
																				k,
																				v: formatValue(
																					(
																						pc.residential_summary as Record<
																							string,
																							unknown
																						>
																					)[
																						k
																					]
																				),
																			})
																		)
																		.filter(
																			({
																				v,
																			}) =>
																				v !==
																				na
																		)
																: [];
														const buildingKeys = [
															"style",
															"condition",
															"year_built",
															"number_of_stories",
															"number_of_bedrooms",
															"number_of_full_baths",
															"exterior_wall",
															"roof_type",
															"heat_type",
															"air_conditioning",
														] as const;
														const building =
															pc.residential_ag_building
																? buildingKeys
																		.map(
																			(
																				k
																			) => ({
																				k,
																				v: formatValue(
																					(
																						pc.residential_ag_building as Record<
																							string,
																							unknown
																						>
																					)[
																						k
																					]
																				),
																			})
																		)
																		.filter(
																			({
																				v,
																			}) =>
																				v !==
																				na
																		)
																: [];
														const hasAny =
															resSummary.length >
																0 ||
															building.length > 0;
														if (!hasAny)
															return null;
														return (
															<div className="bg-white rounded-lg border border-gray-200 w-full">
																<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
																	<h3 className="text-[11px] font-semibold text-gray-900">
																		Building
																		&
																		Property
																		Characteristics
																	</h3>
																</div>
																<div className="p-6 space-y-6">
																	{resSummary.length >
																		0 && (
																		<div>
																			<h4 className="text-base font-semibold text-gray-900 mb-3">
																				Residential
																				Summary
																			</h4>
																			<div className="grid grid-cols-2 gap-4">
																				{resSummary.map(
																					({
																						k,
																						v,
																					}) => (
																						<div
																							key={
																								k
																							}
																							className="flex justify-between py-2 border-b border-gray-100"
																						>
																							<span className="text-xs text-gray-600 capitalize">
																								{k.replace(
																									"_",
																									" "
																								)}
																							</span>
																							<span className="text-xs text-gray-900 font-medium">
																								{
																									v
																								}
																							</span>
																						</div>
																					)
																				)}
																			</div>
																		</div>
																	)}
																	{building.length >
																		0 && (
																		<div>
																			<h4 className="text-base font-semibold text-gray-900 mb-3">
																				Building
																				Details
																			</h4>
																			<div className="grid grid-cols-2 gap-2">
																				{building.map(
																					({
																						k,
																						v,
																					}) => (
																						<div
																							key={
																								k
																							}
																							className="flex flex-col py-2 border-b border-gray-100"
																						>
																							<span className="text-xs text-gray-600">
																								{k.replace(
																									/_/g,
																									" "
																								)}
																							</span>
																							<span className="text-xs text-gray-900 font-medium">
																								{
																									v
																								}
																							</span>
																						</div>
																					)
																				)}
																			</div>
																		</div>
																	)}
																</div>
															</div>
														);
													})()}
												<div className="bg-white rounded-lg border border-gray-200 w-full">
													<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
														<h3 className="text-[11px] font-semibold text-gray-900">
															Recent Sales
														</h3>
													</div>
													{hasRecentSalesData ? (
														<div className="overflow-x-auto">
															<table className="w-full">
																<thead>
																	<tr className="border-b border-gray-200">
																		<th className="text-left py-1.5 px-2 text-[10px] font-semibold text-gray-600">
																			Address
																		</th>
																		<th className="text-left py-1.5 px-2 text-[10px] font-semibold text-gray-600">
																			Parcel
																			#
																		</th>
																		<th className="text-left py-1.5 px-2 text-[10px] font-semibold text-gray-600">
																			Sale
																			Date
																		</th>
																		<th className="text-right py-1.5 px-2 text-[10px] font-semibold text-gray-600">
																			Sale
																			Amount
																		</th>
																	</tr>
																</thead>
																<tbody>
																	{scrapedParcelData!.recent_sales!.map(
																		(
																			sale,
																			idx
																		) => (
																			<tr
																				key={
																					idx
																				}
																				className="border-b border-gray-100 hover:bg-gray-50"
																			>
																				<td className="py-1.5 px-2 text-xs text-gray-900">
																					{formatValue(
																						sale.address
																					)}
																				</td>
																				<td className="py-1.5 px-2 text-xs text-gray-600 font-mono">
																					{formatValue(
																						sale.parcel_number
																					)}
																				</td>
																				<td className="py-1.5 px-2 text-xs text-gray-600">
																					{formatValue(
																						sale.sale_date
																					)}
																				</td>
																				<td className="py-1.5 px-2 text-xs text-gray-900 font-semibold text-right">
																					{formatSaleAmount(
																						sale.sale_amount
																					)}
																				</td>
																			</tr>
																		)
																	)}
																</tbody>
															</table>
														</div>
													) : (
														<div className="flex flex-col items-center justify-center py-10 px-4 text-center">
															<TrendingUp className="w-8 h-8 text-gray-300 mb-2" />
															<p className="text-xs font-medium text-gray-600">
																No recent sales
																data available
															</p>
														</div>
													)}
												</div>
												<div className="bg-white rounded-lg border border-gray-200 w-full">
													<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
														<h3 className="text-[11px] font-semibold text-gray-900">
															Deed History
														</h3>
													</div>
													{hasDeedData ? (
														<div className="overflow-x-auto">
															<table className="w-full">
																<thead>
																	<tr className="border-b border-gray-200">
																		<th className="text-left py-1.5 px-2 text-[10px] font-semibold text-gray-600">
																			Deed
																		</th>
																		<th className="text-left py-1.5 px-2 text-[10px] font-semibold text-gray-600">
																			Grantee
																		</th>
																		<th className="text-left py-1.5 px-2 text-[10px] font-semibold text-gray-600">
																			Sale
																			Date
																		</th>
																		<th className="text-right py-1.5 px-2 text-[10px] font-semibold text-gray-600">
																			Sale
																			Amount
																		</th>
																		<th className="text-center py-1.5 px-2 text-[10px] font-semibold text-gray-600">
																			Multi
																		</th>
																	</tr>
																</thead>
																<tbody>
																	{deedHistory!.map(
																		(
																			deed,
																			idx
																		) => (
																			<tr
																				key={
																					idx
																				}
																				className="border-b border-gray-100 hover:bg-gray-50"
																			>
																				<td className="py-1.5 px-2 text-xs text-gray-900 font-mono">
																					{formatValue(
																						deed.deed
																					)}
																				</td>
																				<td className="py-1.5 px-2 text-xs text-gray-900">
																					{formatValue(
																						deed.grantee
																					)}
																				</td>
																				<td className="py-1.5 px-2 text-xs text-gray-600">
																					{formatValue(
																						deed.sale_date
																					)}
																				</td>
																				<td className="py-1.5 px-2 text-xs text-gray-900 font-semibold text-right">
																					{formatSaleAmount(
																						deed.sale_amount
																					)}
																				</td>
																				<td className="py-1.5 px-2 text-xs text-gray-600 text-center">
																					{formatValue(
																						deed.multi
																					)}
																				</td>
																			</tr>
																		)
																	)}
																</tbody>
															</table>
														</div>
													) : (
														<div className="flex flex-col items-center justify-center py-10 px-4 text-center">
															<FileText className="w-8 h-8 text-gray-300 mb-2" />
															<p className="text-xs font-medium text-gray-600">
																No deed history
																available
															</p>
														</div>
													)}
												</div>
											</div>
										)}

									{/* Tab: Links */}
									{activeTab === "links" && (
										<div className="space-y-6">
											<div className="bg-white rounded-lg border border-gray-200 w-full">
												<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
													<h3 className="text-[11px] font-semibold text-gray-900">
														View on Real Estate
														Platforms
													</h3>
												</div>
												<div className="p-6 grid grid-cols-1 gap-2">
													<a
														href={`https://www.zillow.com/homes/${encodeURIComponent(fullAddress)}_rb/`}
														target="_blank"
														rel="noopener noreferrer"
														className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-blue-50 transition-colors text-sm group/link"
													>
														<div className="flex items-center gap-2">
															<Home className="w-4 h-4 text-blue-600" />
															<span className="text-gray-900 font-medium">
																Zillow
															</span>
														</div>
														<ExternalLink className="w-4 h-4 text-gray-400 group-hover/link:text-blue-600" />
													</a>
													<a
														href={`https://www.realtor.com/realestateandhomes-search/${encodeURIComponent(fullAddress)}`}
														target="_blank"
														rel="noopener noreferrer"
														className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-red-50 transition-colors text-sm group/link"
													>
														<div className="flex items-center gap-2">
															<MapPin className="w-4 h-4 text-red-600" />
															<span className="text-gray-900 font-medium">
																Realtor.com
															</span>
														</div>
														<ExternalLink className="w-4 h-4 text-gray-400 group-hover/link:text-red-600" />
													</a>
												</div>
											</div>
											{scrapedParcelData?.section_urls && (
												<div className="bg-white rounded-lg border border-gray-200 w-full">
													<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
														<h3 className="text-[11px] font-semibold text-gray-900">
															County Resources
														</h3>
													</div>
													<div className="p-6 grid grid-cols-1 gap-2">
														{scrapedParcelData
															.section_urls
															.tax_bill && (
															<a
																href={
																	scrapedParcelData
																		.section_urls
																		.tax_bill
																}
																target="_blank"
																rel="noopener noreferrer"
																className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 text-sm"
															>
																<span className="text-gray-900">
																	View Tax
																	Bill
																</span>
																<ExternalLink className="w-4 h-4 text-gray-400" />
															</a>
														)}
														{scrapedParcelData
															.section_urls
															.view_map && (
															<a
																href={
																	scrapedParcelData
																		.section_urls
																		.view_map
																}
																target="_blank"
																rel="noopener noreferrer"
																className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 text-sm"
															>
																<span className="text-gray-900">
																	View Map
																</span>
																<ExternalLink className="w-4 h-4 text-gray-400" />
															</a>
														)}
														{scrapedParcelData
															.section_urls
															.recent_sales && (
															<a
																href={
																	scrapedParcelData
																		.section_urls
																		.recent_sales
																}
																target="_blank"
																rel="noopener noreferrer"
																className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 text-sm"
															>
																<span className="text-gray-900">
																	View Recent
																	Sales
																</span>
																<ExternalLink className="w-4 h-4 text-gray-400" />
															</a>
														)}
														{scrapedParcelData
															.section_urls
															.property_characteristics && (
															<a
																href={
																	scrapedParcelData
																		.section_urls
																		.property_characteristics
																}
																target="_blank"
																rel="noopener noreferrer"
																className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 text-sm"
															>
																<span className="text-gray-900">
																	Property
																	Characteristics
																</span>
																<ExternalLink className="w-4 h-4 text-gray-400" />
															</a>
														)}
														{scrapedParcelData
															.section_urls
															.tax_and_sewer_only && (
															<a
																href={
																	scrapedParcelData
																		.section_urls
																		.tax_and_sewer_only
																}
																target="_blank"
																rel="noopener noreferrer"
																className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 text-sm"
															>
																<span className="text-gray-900">
																	Check Tax
																	And Sewer
																</span>
																<ExternalLink className="w-4 h-4 text-gray-400" />
															</a>
														)}
														{scrapedParcelData
															.section_urls
															.code_violation_fee && (
															<a
																href={
																	scrapedParcelData
																		.section_urls
																		.code_violation_fee
																}
																target="_blank"
																rel="noopener noreferrer"
																className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 text-sm"
															>
																<span className="text-gray-900">
																	Code
																	Violation
																	Fee
																</span>
																<ExternalLink className="w-4 h-4 text-gray-400" />
															</a>
														)}
														{scrapedParcelData
															.section_urls
															.code_violation_details && (
															<a
																href={
																	scrapedParcelData
																		.section_urls
																		.code_violation_details
																}
																target="_blank"
																rel="noopener noreferrer"
																className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 text-sm"
															>
																<span className="text-gray-900">
																	Code
																	Violation
																	Details
																</span>
																<ExternalLink className="w-4 h-4 text-gray-400" />
															</a>
														)}
														{scrapedParcelData
															.section_urls
															.construction_violation_details && (
															<a
																href={
																	scrapedParcelData
																		.section_urls
																		.construction_violation_details
																}
																target="_blank"
																rel="noopener noreferrer"
																className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 text-sm"
															>
																<span className="text-gray-900">
																	Construction
																	Violation
																	Details
																</span>
																<ExternalLink className="w-4 h-4 text-gray-400" />
															</a>
														)}
														{scrapedParcelData
															.section_urls
															.elevator_certificate && (
															<a
																href={
																	scrapedParcelData
																		.section_urls
																		.elevator_certificate
																}
																target="_blank"
																rel="noopener noreferrer"
																className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 text-sm"
															>
																<span className="text-gray-900">
																	Elevator
																	Certificate
																</span>
																<ExternalLink className="w-4 h-4 text-gray-400" />
															</a>
														)}
													</div>
												</div>
											)}
										</div>
									)}
								</div>
							</div>
						</div>

						{/* Footer */}
						<div className="bg-white border-t border-gray-200 px-6 py-2.5 flex items-center justify-between h-[44px] flex-shrink-0">
							<div className="flex items-center gap-2">
								<Button
									type="button"
									variant="outline"
									size="sm"
									data-testid="export-csv-button"
									disabled={!isSubscribed}
									title={
										!isSubscribed
											? "Subscribe to export parcel data"
											: undefined
									}
									onClick={() => {
										if (!isSubscribed) {
											toast.error(
												"Subscribe to export parcel data"
											);
											return;
										}
										const formatValueForCsv = (
											v: unknown
										): string => {
											if (v === null || v === undefined)
												return "";
											if (typeof v === "object") {
												const obj = v as Record<
													string,
													unknown
												>;
												if (
													typeof obj.address ===
													"string"
												)
													return obj.address;
												if (
													typeof obj.value ===
													"string"
												)
													return obj.value;
												if (
													typeof obj.name === "string"
												)
													return obj.name;
												if (
													typeof obj.text === "string"
												)
													return obj.text;
											}
											if (typeof v === "string") {
												const trimmed = v.trim();
												if (
													trimmed.startsWith("{") &&
													trimmed.endsWith("}")
												) {
													try {
														const parsed =
															JSON.parse(
																trimmed
															) as Record<
																string,
																unknown
															>;
														if (
															typeof parsed.address ===
															"string"
														)
															return parsed.address;
														if (
															typeof parsed.value ===
															"string"
														)
															return parsed.value;
														if (
															typeof parsed.name ===
															"string"
														)
															return parsed.name;
														if (
															typeof parsed.text ===
															"string"
														)
															return parsed.text;
													} catch {
														// not valid JSON, use as-is
													}
												}
												return v;
											}
											return String(v);
										};
										const rows = [
											["Field", "Value"],
											...Object.entries(parcelData).map(
												([k, v]) => [
													getColumnLabel(k),
													formatValueForCsv(v),
												]
											),
										];
										const csv = rows
											.map((r) =>
												r
													.map(
														(c) =>
															`"${String(c).replace(/"/g, '""')}"`
													)
													.join(",")
											)
											.join("\n");
										const a = document.createElement("a");
										a.href = URL.createObjectURL(
											new Blob([csv], {
												type: "text/csv",
											})
										);
										a.download = `parcel-${apn.replace(/\s/g, "-")}.csv`;
										a.click();
										URL.revokeObjectURL(a.href);
									}}
									className="!border-gray-300 !bg-white !text-gray-800 hover:!bg-gray-50 h-7 text-xs px-3 disabled:!opacity-60 disabled:!cursor-not-allowed disabled:hover:!bg-white"
								>
									<Download className="w-3.5 h-3.5 mr-1.5" />
									Export to CSV
								</Button>
							</div>
							<div className="text-xs text-gray-500 truncate">
								{(parcelData as { ll_last_refresh?: string })
									.ll_last_refresh
									? `Data provided by LoveLand • Last refreshed: ${(parcelData as { ll_last_refresh?: string }).ll_last_refresh}`
									: "Data provided by LoveLand"}
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>
			{reqPhotosModal && (
				<RequestPhotosModal
					isOpen={reqPhotosModal}
					onClose={() => setReqPhotosModal(false)}
					properties={
						realEstateId
							? [
									{
										id: realEstateId,
										address: String(
											parcelData.address ?? ""
										).trim(),
										city: String(
											parcelData.scity ?? ""
										).trim(),
										state: String(
											parcelData.state2 ?? ""
										).trim(),
										county: String(
											parcelData.county ?? ""
										).trim(),
										zip: String(
											parcelData.szip5 ?? ""
										).trim(),
										latitude: Number(parcelData.lat ?? 0),
										longitude: Number(parcelData.lon ?? 0),
									},
								]
							: []
					}
				/>
			)}

			{selectedPhoto && (
				<Dialog open={true} onOpenChange={() => setSelectedPhoto(null)}>
					<DialogContent className="max-w-[95vw] w-auto max-h-[95vh] p-0 bg-black border-none">
						<DialogTitle className="sr-only">
							Photo Viewer
						</DialogTitle>
						<DialogDescription className="sr-only">
							Property photo
						</DialogDescription>
						<button
							onClick={() => setSelectedPhoto(null)}
							className="absolute top-4 right-4 z-50 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
							aria-label="Close"
						>
							<X className="w-6 h-6 text-white" />
						</button>
						<div className="relative w-[90vw] h-[85vh]">
							<Image
								fill
								src={MEDIA_BASE_URL + selectedPhoto}
								alt="Property photo"
								className="object-contain"
							/>
						</div>
					</DialogContent>
				</Dialog>
			)}
		</>
	);
}
